import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Download, FileText, X, Save, BookOpen, GripVertical, Sparkles, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const SKILL_LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];
const DRILL_TYPES = ["Individual", "Partner", "Shadow", "Bag"];

const blank = {
  title: "", focus: "", skill_level: "All", duration_mins: 60, date: "",
  objective: "", warm_up: [], main_lesson_notes: "",
  drills: [], cool_down_notes: "", takeaway: "", tags: [],
};

export default function AdminLessons() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState([]); // ordered array of plan ids (drag-reorderable)
  const [bundleTitle, setBundleTitle] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiForm, setAiForm] = useState({
    class_size: 1, bag_count: 1, focus_area: "", duration_mins: 60, skill_level: "All", extra_notes: "",
  });

  const generateWithAI = async () => {
    if (!aiForm.focus_area.trim()) { toast.error("Add a focus area"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/lessons/generate", aiForm);
      const plan = data?.plan || {};
      // Coerce into the editor's `blank` shape so optional fields don't crash the form
      const merged = {
        ...blank, ...plan,
        date: "", // user picks
        tags: Array.isArray(plan.tags) ? plan.tags : [],
        warm_up: Array.isArray(plan.warm_up) ? plan.warm_up : [],
        drills: Array.isArray(plan.drills) ? plan.drills : [],
      };
      setAiOpen(false);
      setEditing(merged);
      toast.success(`Lesson generated — review and save (${merged.drills.length} drills · ${merged.warm_up.length} warm-ups)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed — try again");
    } finally { setAiBusy(false); }
  };

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/lessons");
      setPlans(data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await api.delete(`/admin/lessons/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const downloadPdf = async (id, title) => {
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${apiBase}/api/admin/lessons/${id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(title || "lesson-plan").replace(/[^a-z0-9-]/gi, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF download failed"); }
  };

  const downloadBundle = async () => {
    if (selectedOrder.length === 0) { toast.error("Select at least one lesson"); return; }
    if (!bundleTitle.trim()) { toast.error("Give the bundle a title (e.g., '12-week curriculum')"); return; }
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${apiBase}/api/admin/lessons/bundle-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ plan_ids: selectedOrder, bundle_title: bundleTitle }),
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${bundleTitle.replace(/[^a-z0-9-]/gi, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Bundle PDF generated (${selectedOrder.length} lessons in order)`);
    } catch (err) { toast.error("Bundle generation failed"); }
  };

  const toggleSelect = (id) => {
    setSelectedOrder((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  const removeFromBundle = (id) => setSelectedOrder((cur) => cur.filter((x) => x !== id));

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    setSelectedOrder((cur) => {
      const next = [...cur];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      setDragIdx(i);
      return next;
    });
  };
  const onDragEnd = () => setDragIdx(null);

  const selectedPlans = selectedOrder.map((id) => plans.find((p) => p.id === id)).filter(Boolean);

  if (loading) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-lessons-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display uppercase text-4xl text-white flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-[#FF331F]" /> Lesson Plans
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Build, store, and export class plans. Combine multiple into a single PDF bundle.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAiOpen(true)} variant="outline" className="border-amber-700/60 bg-amber-900/10 hover:bg-amber-900/20 text-amber-200 font-bold uppercase tracking-widest rounded-sm" data-testid="ai-generate-btn">
            <Sparkles className="h-4 w-4 mr-2" /> Generate with AI
          </Button>
          <Button onClick={() => setEditing({ ...blank })} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="new-lesson-btn">
            <Plus className="h-4 w-4 mr-2" /> New plan
          </Button>
        </div>
      </div>

      {/* Bundle controls */}
      {plans.length > 0 && (
        <div className="border border-[#27272A] bg-[#0F0F0F] mb-6" data-testid="bundle-controls">
          <div className="p-4 flex flex-wrap items-center gap-3 border-b border-[#27272A]">
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
              Bundle ({selectedOrder.length} selected)
            </span>
            <Input placeholder="Bundle title (e.g., 12-week Curriculum)" value={bundleTitle}
              onChange={(e) => setBundleTitle(e.target.value)}
              className="bg-[#050505] border-[#27272A] text-white rounded-sm flex-1 max-w-md" data-testid="bundle-title" />
            <Button onClick={downloadBundle} disabled={selectedOrder.length === 0 || !bundleTitle.trim()}
              className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="bundle-download">
              <FileText className="h-4 w-4 mr-2" /> Generate bundle PDF
            </Button>
            {selectedOrder.length > 0 && (
              <button onClick={() => setSelectedOrder([])} className="text-zinc-500 text-xs hover:text-white">Clear</button>
            )}
          </div>

          {/* Reorderable selection list — only shows when something's selected */}
          {selectedOrder.length > 0 && (
            <div className="p-4 bg-[#0A0A0A]" data-testid="bundle-order-list">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">
                Drag to reorder · the bundle PDF will follow this sequence
              </div>
              <div className="space-y-1.5">
                {selectedPlans.map((p, i) => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={(e) => onDragOver(e, i)}
                    onDragEnd={onDragEnd}
                    className={`flex items-center gap-3 px-3 py-2 border border-[#27272A] bg-[#0F0F0F] cursor-move transition-opacity ${dragIdx === i ? "opacity-40" : "hover:bg-[#161616]"}`}
                    data-testid={`bundle-item-${p.id}`}
                  >
                    <GripVertical className="h-4 w-4 text-zinc-500 shrink-0" />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold w-8">#{i + 1}</span>
                    <span className="text-white text-sm flex-1 truncate">{p.title}</span>
                    {p.focus && <span className="text-zinc-500 text-xs hidden md:inline">{p.focus}</span>}
                    {p.skill_level && <span className="text-[9px] uppercase tracking-widest text-zinc-500 bg-[#1A1A1A] border border-[#27272A] px-1.5 py-0.5">{p.skill_level}</span>}
                    <button onClick={() => removeFromBundle(p.id)} className="text-zinc-500 hover:text-red-500 shrink-0"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {plans.length === 0 ? (
        <div className="border border-[#27272A] bg-[#0F0F0F] p-10 text-center">
          <BookOpen className="h-12 w-12 text-[#FF331F] mx-auto mb-3" />
          <h2 className="font-display uppercase text-2xl text-white mb-2">No lesson plans yet</h2>
          <p className="text-zinc-400 text-sm mb-6">Create your first plan — title, focus, drills, coach&apos;s cues — and export as a clean printable PDF.</p>
          <Button onClick={() => setEditing({ ...blank })} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm">
            <Plus className="h-4 w-4 mr-2" /> Create lesson plan
          </Button>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0F0F0F]">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="p-3 w-8"></th>
                <th className="p-3">Title</th>
                <th className="p-3">Focus</th>
                <th className="p-3">Level</th>
                <th className="p-3">Duration</th>
                <th className="p-3">Drills</th>
                <th className="p-3">Date</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`lesson-row-${p.id}`}>
                  <td className="p-3">
                    <Checkbox checked={selectedOrder.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)}
                      className="border-zinc-600 data-[state=checked]:bg-[#FF331F]" data-testid={`select-${p.id}`} />
                  </td>
                  <td className="p-3 text-white font-bold">{p.title}</td>
                  <td className="p-3 text-zinc-400">{p.focus || "—"}</td>
                  <td className="p-3 text-zinc-400">{p.skill_level}</td>
                  <td className="p-3 text-zinc-400">{p.duration_mins ? `${p.duration_mins} min` : "—"}</td>
                  <td className="p-3 text-zinc-400">{(p.drills || []).length}</td>
                  <td className="p-3 text-zinc-500 text-xs">{p.date || "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => downloadPdf(p.id, p.title)} className="text-zinc-400 hover:text-[#FF331F]" title="Download PDF" data-testid={`download-${p.id}`}><Download className="h-4 w-4" /></button>
                      <button onClick={() => setEditing(p)} className="text-zinc-400 hover:text-[#FF331F]" data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(p.id, p.title)} className="text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <LessonDialog plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <Dialog open={aiOpen} onOpenChange={(v) => !v && !aiBusy && setAiOpen(false)}>
        <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-xl" data-testid="ai-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" /> Generate Lesson Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-zinc-400">Claude Sonnet 4.6 will draft a complete plan from these inputs. You can edit anything before saving.</p>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Focus area</Label>
              <Input value={aiForm.focus_area}
                onChange={(e) => setAiForm({ ...aiForm, focus_area: e.target.value })}
                placeholder="e.g. Jab + footwork · Slipping & countering · Conditioning circuit"
                className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                data-testid="ai-focus" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Class size</Label>
                <Input type="number" min="1" max="20" value={aiForm.class_size}
                  onChange={(e) => setAiForm({ ...aiForm, class_size: Math.max(1, Number(e.target.value) || 1) })}
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                  data-testid="ai-class-size" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Heavy bags</Label>
                <Input type="number" min="0" max="20" value={aiForm.bag_count}
                  onChange={(e) => setAiForm({ ...aiForm, bag_count: Math.max(0, Number(e.target.value) || 0) })}
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                  data-testid="ai-bag-count" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Duration (min)</Label>
                <Input type="number" min="15" max="180" value={aiForm.duration_mins}
                  onChange={(e) => setAiForm({ ...aiForm, duration_mins: Math.max(15, Number(e.target.value) || 60) })}
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                  data-testid="ai-duration" />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Skill level</Label>
              <Select value={aiForm.skill_level} onValueChange={(v) => setAiForm({ ...aiForm, skill_level: v })}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ai-skill"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                  {SKILL_LEVELS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Extra notes (optional)</Label>
              <Textarea rows={2} value={aiForm.extra_notes}
                onChange={(e) => setAiForm({ ...aiForm, extra_notes: e.target.value })}
                placeholder="Any quirks — injuries, limited space, particular weakness to address…"
                className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                data-testid="ai-notes" />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-[#27272A]">
              <Button variant="outline" disabled={aiBusy} onClick={() => setAiOpen(false)}
                className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white rounded-sm">Cancel</Button>
              <Button onClick={generateWithAI} disabled={aiBusy}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest rounded-sm"
                data-testid="ai-generate-submit">
                {aiBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate</>}
              </Button>
            </div>
            <p className="text-[10px] text-zinc-600 text-center">Generation takes 10-30 seconds. The draft opens in the editor — nothing saves until you click Save.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LessonDialog({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const isNew = !plan.id;

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/lessons", form);
      else await api.patch(`/admin/lessons/${plan.id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const saveAsTodaysPlan = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setEmailing(true);
    try {
      const { data } = await api.post("/admin/lessons/save-as-todays-plan", form);
      if (data?.email_sent) {
        toast.success("Saved as today's plan + emailed to you with PDF attached.");
      } else {
        toast.success("Saved as today's plan — email skipped (Resend not configured or failed silently).");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save + email failed");
    } finally { setEmailing(false); }
  };

  // Generic list field for warm_up + drills
  const updateAt = (key, idx, patch) => {
    const next = [...(form[key] || [])];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, [key]: next });
  };
  const addItem = (key, blankItem) => setForm({ ...form, [key]: [...(form[key] || []), blankItem] });
  const removeAt = (key, idx) => {
    const next = [...(form[key] || [])]; next.splice(idx, 1);
    setForm({ ...form, [key]: next });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="lesson-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{isNew ? "New lesson plan" : `Edit: ${plan.title}`}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {/* Header */}
          <section className="border border-[#27272A] bg-[#050505] p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Header</div>
            <Field label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Jab Fundamentals — Day 1" className="bg-[#0F0F0F] border-[#27272A] rounded-sm" data-testid="lesson-title" /></Field>
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Focus / topic"><Input value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} placeholder="Jab mechanics" className="bg-[#0F0F0F] border-[#27272A] rounded-sm" /></Field>
              <Field label="Skill level">
                <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
                  <SelectTrigger className="bg-[#0F0F0F] border-[#27272A] rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                    {SKILL_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Duration (min)"><Input type="number" value={form.duration_mins || ""} onChange={(e) => setForm({ ...form, duration_mins: parseInt(e.target.value || "0", 10) })} className="bg-[#0F0F0F] border-[#27272A] rounded-sm" /></Field>
              <Field label="Date"><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0F0F0F] border-[#27272A] rounded-sm" /></Field>
            </div>
            <Field label="Objective (what students should learn)"><Textarea rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="Build a textbook jab — proper stance, alignment, and snap." className="bg-[#0F0F0F] border-[#27272A] rounded-sm" /></Field>
          </section>

          {/* Warm-up */}
          <section className="border border-[#27272A] bg-[#050505] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Warm-up</div>
              <Button type="button" onClick={() => addItem("warm_up", { name: "", duration_mins: null, notes: "" })} variant="outline" size="sm" className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> Add</Button>
            </div>
            {(form.warm_up || []).map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input value={it.name} onChange={(e) => updateAt("warm_up", i, { name: e.target.value })} placeholder="Shadow boxing" className="col-span-5 bg-[#0F0F0F] border-[#27272A] rounded-sm" />
                <Input type="number" value={it.duration_mins || ""} onChange={(e) => updateAt("warm_up", i, { duration_mins: parseFloat(e.target.value) || null })} placeholder="min" className="col-span-2 bg-[#0F0F0F] border-[#27272A] rounded-sm" />
                <Input value={it.notes || ""} onChange={(e) => updateAt("warm_up", i, { notes: e.target.value })} placeholder="Notes" className="col-span-4 bg-[#0F0F0F] border-[#27272A] rounded-sm" />
                <button type="button" onClick={() => removeAt("warm_up", i)} className="col-span-1 text-zinc-500 hover:text-red-500"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </section>

          {/* Main lesson */}
          <Field label="Main lesson — how to teach it"><Textarea rows={5} value={form.main_lesson_notes} onChange={(e) => setForm({ ...form, main_lesson_notes: e.target.value })} placeholder="Start with stance — feet shoulder-width...

The jab is a snap, not a push..." className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>

          {/* Drills */}
          <section className="border border-[#27272A] bg-[#050505] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Drills</div>
              <Button type="button" onClick={() => addItem("drills", { name: "", type: "Individual", duration_mins: null, equipment: "", instructions: "", coachs_cues: [], common_mistakes: [], order_index: 100 })} variant="outline" size="sm" className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> Add drill</Button>
            </div>
            {(form.drills || []).map((d, i) => (
              <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-3 space-y-2" data-testid={`drill-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Drill {i + 1}</span>
                  <button type="button" onClick={() => removeAt("drills", i)} className="text-zinc-500 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
                <div className="grid sm:grid-cols-4 gap-2">
                  <Input value={d.name} onChange={(e) => updateAt("drills", i, { name: e.target.value })} placeholder="Mirror jab" className="col-span-2 bg-[#050505] border-[#27272A] rounded-sm" />
                  <Select value={d.type} onValueChange={(v) => updateAt("drills", i, { type: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">{DRILL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" value={d.duration_mins || ""} onChange={(e) => updateAt("drills", i, { duration_mins: parseFloat(e.target.value) || null })} placeholder="Duration" className="bg-[#050505] border-[#27272A] rounded-sm" />
                </div>
                <Input value={d.equipment || ""} onChange={(e) => updateAt("drills", i, { equipment: e.target.value })} placeholder="Equipment (Heavy bag, pads, mirror...)" className="bg-[#050505] border-[#27272A] rounded-sm text-xs" />
                <Textarea rows={2} value={d.instructions} onChange={(e) => updateAt("drills", i, { instructions: e.target.value })} placeholder="Step-by-step instructions" className="bg-[#050505] border-[#27272A] rounded-sm" />
                <BulletList label="Coach's cues (verbal prompts)" items={d.coachs_cues || []} onChange={(v) => updateAt("drills", i, { coachs_cues: v })} placeholder='e.g., "Snap, don&apos;t push"' />
                <BulletList label="Common mistakes to correct" items={d.common_mistakes || []} onChange={(v) => updateAt("drills", i, { common_mistakes: v })} placeholder="e.g., Dropping the rear hand" />
              </div>
            ))}
          </section>

          {/* Cool-down + takeaway */}
          <Field label="Cool-down notes"><Textarea rows={3} value={form.cool_down_notes} onChange={(e) => setForm({ ...form, cool_down_notes: e.target.value })} placeholder="Light shadow + breathing. Stretches." className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>
          <Field label="Takeaway (closing thought)"><Input value={form.takeaway} onChange={(e) => setForm({ ...form, takeaway: e.target.value })} placeholder="If your jab is solid, everything else opens up." className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>

          <div className="flex items-center justify-end gap-2 border-t border-[#27272A] pt-4 flex-wrap">
            <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
            {isNew && (
              <Button onClick={saveAsTodaysPlan} disabled={emailing || saving}
                variant="outline"
                className="border-amber-700/60 bg-amber-900/10 hover:bg-amber-900/20 text-amber-200 font-bold uppercase tracking-widest text-xs rounded-sm"
                data-testid="lesson-save-and-email">
                {emailing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving + emailing…</> : <><Mail className="h-4 w-4 mr-2" /> Save as today&apos;s plan + email me</>}
              </Button>
            )}
            <Button onClick={save} disabled={saving || emailing} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="lesson-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label><div className="mt-1">{children}</div></div>;
}

function BulletList({ label, items, onChange, placeholder }) {
  const update = (i, v) => { const next = [...items]; next[i] = v; onChange(next); };
  const add = () => onChange([...items, ""]);
  const remove = (i) => { const next = [...items]; next.splice(i, 1); onChange(next); };
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</Label>
      <div className="space-y-1.5 mt-1">
        {items.map((it, i) => (
          <div key={i} className="flex gap-1.5">
            <Input value={it} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} className="bg-[#050505] border-[#27272A] rounded-sm text-xs" />
            <button type="button" onClick={() => remove(i)} className="text-zinc-500 hover:text-red-500"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <Button type="button" onClick={add} variant="outline" size="sm" className="border-[#27272A] bg-[#050505] hover:bg-[#1A1A1A] rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> Add</Button>
      </div>
    </div>
  );
}
